import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import apiInstance from "../../utility/apiInstance";

const hasNumber = (value: string) => /[0-9]/.test(value);
const hasMixed = (value: string) => /[a-z]/.test(value) && /[A-Z]/.test(value);
const hasSpecial = (value: string) => /[!#@$%^&*)(+=._-]/.test(value);

const strengthColor = (count: number): string => {
    if (count < 1) return "poor";
    if (count < 2) return "weak";
    if (count < 3) return "strong";
    return "heavy";
};

const strengthIndicator = (value: string): number => {
    let strengths = 0;
    if (value.length >= 8) strengths = 1;
    if (hasNumber(value) && value.length >= 8) strengths = 2;
    if (hasSpecial(value) && value.length >= 8 && hasNumber(value)) strengths = 3;
    if (hasMixed(value) && hasSpecial(value) && value.length >= 8 && hasNumber(value)) strengths = 3;
    return strengths;
};

const ChangePasswordForm: React.FC = () => {
    const token = useSelector((state: any) => state.auth.token);

    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [validationError, setValidationError] = useState(0);
    const [strength, setStrength] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [msg, setMsg] = useState({ error: false, loading: false, text: "" });
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const validatePassword = (value: string) => {
        if (!value) setValidationError(1);
        else if (value.length < 8) setValidationError(2);
        else if (!/[0-9]/.test(value)) setValidationError(3);
        else if (!/[!@#$%^&*()]/.test(value)) setValidationError(4);
        else setValidationError(5);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setPassword(val);
        validatePassword(val);
        if (errorFields.includes('password')) {
            setErrorFields((prev) => prev.filter((f) => f !== 'password'));
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.password;
                return next;
            });
        }
    };

    useEffect(() => {
        setStrength(password ? strengthColor(strengthIndicator(password)) : "");
    }, [password]);

    const strengthMessage = () => {
        switch (validationError) {
            case 2: return <span className="active mt-2" style={{ fontSize: 14, color: "#DC3545" }}>Weak. Must contain at least 8 characters</span>;
            case 3: return <span className="active mt-2" style={{ fontSize: 14, color: "#FFC107" }}>Average. Must contain at least 1 number</span>;
            case 4: return <span className="active mt-2" style={{ fontSize: 14, color: "#0D6EFD" }}>Almost. Must contain a special symbol</span>;
            case 5: return <span className="active mt-2" style={{ fontSize: 14, color: "#4BB543" }}>Awesome! You have a secure password.</span>;
            default: return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const invalid: string[] = [];
        const messages: Record<string, string> = {};
        if (!currentPassword || !password || !confirmPassword) {
            if (!currentPassword) {
                invalid.push('currentPassword');
                messages.currentPassword = 'Current password is required.';
            }
            if (!password) {
                invalid.push('password');
                messages.password = 'New password is required.';
            }
            if (!confirmPassword) {
                invalid.push('confirmPassword');
                messages.confirmPassword = 'Confirm password is required.';
            }
        } else if (password === currentPassword) {
            invalid.push('password');
            messages.password = 'New password cannot be the same as your current password.';
        } else if (password !== confirmPassword) {
            invalid.push('password', 'confirmPassword');
            messages.password = 'New password and confirm password do not match.';
            messages.confirmPassword = 'New password and confirm password do not match.';
        }

        if (invalid.length) {
            setErrorFields(invalid);
            setFieldErrors(messages);
            setMsg({ error: false, loading: false, text: "" });
            return;
        }

        setErrorFields([]);
        setFieldErrors({});
        setMsg({ error: false, loading: true, text: "" });

        try {
            await apiInstance.post(
                "/api/users/change-password",
                { currentPassword, password },
                { headers: { Authorization: `JWT ${token}` } }
            );
            setMsg({ error: false, loading: false, text: "Password updated successfully!" });
            setCurrentPassword("");
            setPassword("");
            setConfirmPassword("");
            setValidationError(0);
            setStrength("");
        } catch (err: any) {
            const message =
                err?.response?.data?.errors?.[0]?.message ||
                err?.message ||
                "Failed to update password.";
            setMsg({ error: true, loading: false, text: message });
        }
    };

    return (
        <div className="card mb-0">
            <div className="card-body">
                <div className="mb-4 pb-2">
                    <h5 className="mb-1 fs-18">Change Password</h5>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-8">
                            {/* Current Password */}
                            <div className="mb-3 position-relative">
                                <label className="form-label">
                                    Current Password <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative">
                                    <input
                                        type={showCurrent ? "text" : "password"}
                                        className={`form-control form-control-lg pass-input ${errorFields.includes('currentPassword') ? 'is-invalid' : ''}`}
                                        value={currentPassword}
                                        onChange={(e) => {
                                            setCurrentPassword(e.target.value);
                                            if (errorFields.includes('currentPassword')) {
                                                setErrorFields((prev) => prev.filter((f) => f !== 'currentPassword'));
                                                setFieldErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.currentPassword;
                                                    return next;
                                                });
                                            }
                                        }}
                                    />
                                    <span
                                        className="input-icon-addon toggle-password fs-14"
                                        onClick={() => setShowCurrent((p) => !p)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <i className={`isax ${showCurrent ? "isax-eye" : "isax-eye-slash"}`} />
                                    </span>
                                </div>
                                {fieldErrors.currentPassword && (
                                    <div className="invalid-feedback d-block">{fieldErrors.currentPassword}</div>
                                )}
                            </div>

                            {/* New Password */}
                            <div className="mb-3 position-relative">
                                <label className="form-label">
                                    New Password <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative" id="passwordInput">
                                    <input
                                        className={`form-control pass-input ${errorFields.includes('password') ? 'is-invalid' : ''}`}
                                        type={showNew ? "text" : "password"}
                                        value={password}
                                        onChange={handlePasswordChange}
                                    />
                                    <span
                                        className={`toggle-passwords text-gray-7 fs-14 isax ${showNew ? "isax-eye" : "isax-eye-slash"}`}
                                        onClick={() => setShowNew((p) => !p)}
                                        style={{ cursor: "pointer" }}
                                    />
                                </div>
                                <div
                                    id="passwordStrength"
                                    style={{ display: "flex" }}
                                    className={`password-strength ${
                                        strength === "poor" ? "poor-active"
                                        : strength === "weak" ? "avg-active"
                                        : strength === "strong" ? "strong-active"
                                        : strength === "heavy" ? "heavy-active"
                                        : ""
                                    }`}
                                >
                                    <span id="poor" className="active" />
                                    <span id="weak" className="active" />
                                    <span id="strong" className="active" />
                                    <span id="heavy" className="active" />
                                </div>
                                <div id="passwordInfo">{strengthMessage()}</div>
                                {fieldErrors.password && (
                                    <div className="invalid-feedback d-block">{fieldErrors.password}</div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div className="mb-3 position-relative">
                                <label className="form-label">
                                    Confirm Password <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative">
                                    <input
                                        type={showConfirm ? "text" : "password"}
                                        className={`pass-inputa form-control form-control-lg ${errorFields.includes('confirmPassword') ? 'is-invalid' : ''}`}
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            if (errorFields.includes('confirmPassword')) {
                                                setErrorFields((prev) => prev.filter((f) => f !== 'confirmPassword'));
                                                setFieldErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.confirmPassword;
                                                    return next;
                                                });
                                            }
                                        }}
                                    />
                                    <span
                                        className={`isax toggle-passworda ${showConfirm ? "isax-eye" : "isax-eye-slash"} text-gray-7 fs-14`}
                                        onClick={() => setShowConfirm((p) => !p)}
                                        style={{ cursor: "pointer", position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                                    />
                                </div>
                                {fieldErrors.confirmPassword && (
                                    <div className="invalid-feedback d-block">{fieldErrors.confirmPassword}</div>
                                )}
                            </div>

                            <div>
                                <button className="btn btn-secondary" type="submit" disabled={msg.loading}>
                                    {msg.loading ? "Updating..." : "Change Password"}
                                </button>
                                {!msg.loading && msg.text && (
                                    <span className={`mt-3 fs-14 fw-normal d-flex align-items-center ${msg.error ? "text-danger" : "text-success"}`}>
                                        {msg.text}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChangePasswordForm;
